import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ApiClient } from '../../api';
import { Detailkarte } from './Detailkarte';
import { LOESUNGSART_ZUSTAENDIG, STATUS, ZUSTANDSFARBE, farbzustand, z } from './gemeinsam';
import {
  ConfirmDialog, DataTable, EmptyState, Feld, FormSection, LoadingState,
  StatusBadge, type Spalte,
} from '../../muster';

/** Dieselben Felder wie `KoordinationEndpoints.KiBewertungAnzeige` (Server, Dokument 36 Abschnitt C) — an der Zeile aus `r.kiBewertung` gelesen. */
interface KiBewertungZeile {
  planId: string;
  status: string;
  bewertung: string | null;
  massnahme: string | null;
  weicher: string | null;
  weicherUniqueId: string | null;
  richtung: string | null;
  betragMm: string | null;
  risiko: string | null;
  zuversicht: string | null;
  begruendung: string | null;
  pruefpunkte: string[];
  angelegtAm: string;
}

/**
 * Farbe je Bewertung — dieselbe Ampellogik wie EINSTUFUNG in gemeinsam.ts
 * (echt=rot, unklar=gelb, Rest=grün).
 *
 * Beide Tabellen sind SCHLÜSSEL für `farbzustand()`, kein Stil: gerendert
 * wird immer über `StatusBadge`. Bis 19.09.2026 standen hier vier Hexwerte,
 * die dieselbe Ampel ein viertes Mal aufschrieben; die Stufe "hoch" lag mit
 * #b91c1c zur Marke bei ΔE00 = 3,24. Jetzt dieselben Merkmale wie überall.
 * `farbzustand()` kennt genau diese vier Zeichenketten - ein Tippfehler
 * liefert stillschweigend `neutral`, deshalb kommen sie aus `ZUSTANDSFARBE`
 * und werden nicht noch einmal getippt.
 *
 * Die drei Stufen bleiben nachgemessen unterscheidbar (ΔE00):
 *   hoch ↔ mittel 34,29 · mittel ↔ niedrig 29,70 · hoch ↔ niedrig 59,51.
 */
const BEWERTUNG_FARBE: Record<string, string> = {
  echte_kollision: ZUSTANDSFARBE.fehler,
  scheinkollision: ZUSTANDSFARBE.gut,
  unklar: ZUSTANDSFARBE.warnung,
};

const RISIKO_FARBE: Record<string, string> = {
  High: ZUSTANDSFARBE.fehler,
  Medium: ZUSTANDSFARBE.warnung,
  Low: ZUSTANDSFARBE.gut,
};

/**
 * Die Liste des Planerarbeitsplatzes (Batch 7) — seitenweise Tabelle,
 * Mehrfachauswahl mit Sammelaktionen, und darunter die bestehende
 * Detailkarte (unverändert, hier nur umschlossen mit den vier Knöpfen
 * Angenommen/Umgesetzt/Ablehnen/Nächste — NICHT in Detailkarte.tsx selbst,
 * das bleibt der Vorschaubild-Arbeit des anderen Bearbeiters vorbehalten).
 *
 * Ruft `api.*` NUR über die Rückrufe der Elternkomponente auf (dasselbe
 * Muster wie in Arbeitsvorrat.tsx) — mit einer Ausnahme: `api`/`projektId`
 * werden wie dort nur DURCHGEREICHT an `<Detailkarte>` für deren eigenes
 * Vorschaubild.
 */
export function Kollisionsliste({
  api, projektId,
  zeilen, zeilenGesamt, seite, groesse, onSeiteChange,
  ausgewaehlt, onAuswahlChange,
  ablehngruende, onSammelaktion,
  geoeffnet, onZeileKlick,
  detail, detailLaedt,
  onEntscheiden, onNaechste,
  kiAusgewaehlt, onKiAuswahlChange, kiFortschritt,
  onKiBewertungAnfordern, onKiBewertungAbbrechen,
}: {
  api: ApiClient;
  projektId: string;
  zeilen: any[];
  zeilenGesamt: number;
  seite: number;
  groesse: number;
  onSeiteChange: (s: number) => void;
  ausgewaehlt: Set<string>;
  onAuswahlChange: (neu: Set<string>) => void;
  ablehngruende: any[];
  onSammelaktion: (
    status: 'angenommen' | 'abgelehnt',
    ablehngrund?: string, angabe?: string,
  ) => void;
  geoeffnet: string | null;
  onZeileKlick: (id: string) => void;
  detail: any;
  detailLaedt: string | null;
  onEntscheiden: (status: 'angenommen' | 'umgesetzt' | 'abgelehnt') => void;
  onNaechste: () => void;
  /** Dokument 36, Abschnitt C — Kollisions-Ids (NICHT Anweisungs-Ids) für den KI-Stapel dieser Seite. */
  kiAusgewaehlt: Set<string>;
  onKiAuswahlChange: (neu: Set<string>) => void;
  kiFortschritt: { aktuell: number; gesamt: number } | null;
  onKiBewertungAnfordern: (kollisionIds: string[]) => void;
  onKiBewertungAbbrechen: () => void;
}) {
  const [ablehnenOffen, setAblehnenOffen] = useState(false);
  const [grund, setGrund] = useState('');
  const [angabe, setAngabe] = useState('');
  const [kiOffen, setKiOffen] = useState<string | null>(null);
  const [frage, setFrage] = useState<'angenommen' | 'abgelehnt' | null>(null);
  const kiLaeuft = kiFortschritt !== null;

  const seiten = Math.max(1, Math.ceil(zeilenGesamt / Math.max(1, groesse)));

  // Ausgewählt wird über die ANWEISUNG-Id, nicht die Kollisions-Id -
  // 'anweisungen/sammelstatus' entscheidet Anweisungen. Zeilen ohne
  // Anweisung (noch nicht abgeleitet) bekommen keine Checkbox.
  const anweisungIds = zeilen
    .map((z2) => z2.anweisungId)
    .filter((id): id is string => !!id);

  const alleAnKlicken = () => {
    if (ausgewaehlt.size === anweisungIds.length) {
      onAuswahlChange(new Set());
      return;
    }
    onAuswahlChange(new Set(anweisungIds));
  };

  const einzelnKlicken = (id: string) => {
    const neu = new Set(ausgewaehlt);
    if (neu.has(id)) neu.delete(id); else neu.add(id);
    onAuswahlChange(neu);
  };

  const kiEinzelnKlicken = (id: string) => {
    const neu = new Set(kiAusgewaehlt);
    if (neu.has(id)) neu.delete(id); else neu.add(id);
    onKiAuswahlChange(neu);
  };

  /* ══════════════════════════════════════════════════════════════════════
     ZWEI AUSWAHLEN AUF EINER TABELLE (U6a)

     Die Tabelle trägt zwei voneinander unabhängige Mehrfachauswahlen:

       KI-Bewertung     gilt für JEDE Zeile (Kollisions-Id)
       Sammelstatus     gilt nur für Zeilen MIT Anweisung (Anweisungs-Id)

     `DataTable` kennt genau eine (`auswahl`). Sie bekommt die KI-Auswahl,
     weil deren „alle wählen" wirklich alle Zeilen meint — das ist die
     Bedeutung, die der Haken im Tabellenkopf hat. Die Anweisungsauswahl
     bleibt eine eigene Spalte mit eigenem Kopfhaken, genau wie vorher; sonst
     ließen sich Zeilen ohne Anweisung wählen, und die Sammelaktion täte für
     sie stillschweigend nichts.
     ══════════════════════════════════════════════════════════════════════ */
  const spalten: Spalte<any>[] = useMemo(() => [
    {
      schluessel: 'anweisungswahl', sortierbar: false, breite: '42px',
      immerSichtbar: true,
      titel: '',
      titelHinweis: 'Auswahl für den Sammelstatus der Anweisungen',
      inhalt: (r: any) => (r.anweisungId ? (
        <input
          type="checkbox"
          aria-label={`Anweisung ${r.anweisungTitel ?? r.id} auswählen`}
          checked={ausgewaehlt.has(r.anweisungId)}
          onChange={() => einzelnKlicken(r.anweisungId)}
          onClick={(e) => e.stopPropagation()}
        />
      ) : null),
    },
    {
      schluessel: 'status', titel: 'Status', art: 'status', breite: '150px',
      wert: (r: any) => (STATUS[r.status] ?? STATUS.ohne_anweisung).text,
      inhalt: (r: any) => {
        const st = STATUS[r.status] ?? STATUS.ohne_anweisung;
        return <StatusBadge zustand={farbzustand(st.farbe)} text={st.text} klein />;
      },
    },
    {
      schluessel: 'weicher', titel: 'Weicher', breite: '110px',
      wert: (r: any) => r.weicher?.gewerk ?? '',
      inhalt: (r: any) => r.weicher?.gewerk ?? '—',
    },
    {
      schluessel: 'bleiber', titel: 'Bleiber', breite: '110px',
      wert: (r: any) => r.bleiber?.gewerk ?? '',
      inhalt: (r: any) => r.bleiber?.gewerk ?? '—',
    },
    {
      schluessel: 'durchdringung', titel: 'Durchdringung', art: 'zahl',
      einheit: 'mm', breite: '140px',
      wert: (r: any) => r.durchdringungMm,
    },
    {
      schluessel: 'loesungsart', titel: 'Lösungsart', breite: '150px',
      wert: (r: any) => r.loesungsart ?? '',
      inhalt: (r: any) => (r.loesungsart
        ? (
          <StatusBadge
            zustand={farbzustand(LOESUNGSART_ZUSTAENDIG.weicher.farbe)}
            text={r.loesungsart}
            klein
          />
        )
        : '—'),
    },
    {
      schluessel: 'anweisung', titel: 'Anweisung',
      wert: (r: any) => r.anweisungTitel ?? '',
      inhalt: (r: any) => (
        <span style={{ fontSize: 12 }}>{(r.anweisungTitel ?? '').slice(0, 60)}</span>
      ),
    },
    {
      schluessel: 'quadrant', titel: 'Quadrant', breite: '110px',
      wert: (r: any) => r.quadrant ?? '',
      inhalt: (r: any) => r.quadrant ?? '—',
    },
    {
      schluessel: 'zuletzt', titel: 'Zuletzt', art: 'datum', breite: '110px',
      wert: (r: any) => r.updatedAt ?? '',
      inhalt: (r: any) => (r.updatedAt
        ? new Date(r.updatedAt).toLocaleDateString('de-DE')
        : '—'),
    },
    {
      schluessel: 'ki', titel: 'KI-Bewertung', sortierbar: false, breite: '220px',
      titelHinweis: 'Dokument 36, Abschnitt C',
      inhalt: (r: any) => (
        <KiBewertungZelle
          kiBewertung={r.kiBewertung as KiBewertungZeile | null | undefined}
          projektId={projektId}
          ausgewaehlt={kiAusgewaehlt.has(r.id)}
          onAuswahlToggle={() => kiEinzelnKlicken(r.id)}
          kiLaeuft={kiLaeuft}
          offen={kiOffen === r.id}
          onOffenToggle={() => setKiOffen(kiOffen === r.id ? null : r.id)}
          onAnfordern={() => onKiBewertungAnfordern([r.id])}
        />
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [ausgewaehlt, kiAusgewaehlt, kiLaeuft, kiOffen, projektId]);

  return (
    <>
      <DataTable
        name="Kollisionen"
        spalten={spalten}
        zeilen={zeilen}
        schluessel={(r: any) => r.id}
        // Die Blätterung bleibt auf dem SERVER (50 je Seite); die Tabelle
        // zählt deshalb nicht selbst, sondern bekommt den Text.
        proSeite={0}
        trefferText={`${z(zeilenGesamt)} Kollisionen · Seite ${seite + 1} von ${seiten}`}
        beiZeileKlick={(r: any) => onZeileKlick(r.id)}
        aktiveZeile={geoeffnet ?? undefined}
        suche={false}
        auswahl={{
          gewaehlt: Array.from(kiAusgewaehlt),
          beiAuswahl: (ids) => onKiAuswahlChange(new Set(ids)),
        }}
        auswahlAktionen={(
          <>
            {!kiLaeuft && (
              <button type="button" className="m-knopf"
                onClick={() => onKiBewertungAnfordern(Array.from(kiAusgewaehlt))}>
                KI-Bewertung anfordern (Stapel)
              </button>
            )}
            {kiLaeuft && (
              <>
                <span className="schwach" style={{ fontSize: 12 }}>
                  KI-Bewertung: Kollision {kiFortschritt!.aktuell} von{' '}
                  {kiFortschritt!.gesamt} …
                </span>
                {kiFortschritt!.aktuell < kiFortschritt!.gesamt && (
                  <button type="button" className="m-knopf"
                          onClick={onKiBewertungAbbrechen}>
                    Abbrechen
                  </button>
                )}
              </>
            )}
          </>
        )}
        werkzeuge={(
          <>
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
                            fontSize: 12 }}>
              <input
                type="checkbox"
                checked={anweisungIds.length > 0
                  && ausgewaehlt.size === anweisungIds.length}
                onChange={alleAnKlicken}
              />
              alle Anweisungen
            </label>

            {ausgewaehlt.size > 0 && (
              <>
                <span className="schwach" style={{ fontSize: 12 }}>
                  {ausgewaehlt.size} Anweisungen gewählt
                </span>
                <button type="button" className="m-knopf"
                        onClick={() => setFrage('angenommen')}>
                  Angenommen
                </button>
                <button type="button" className="m-knopf"
                        onClick={() => setAblehnenOffen((o) => !o)}>
                  Ablehnen mit Grund
                </button>
              </>
            )}

            <button type="button" className="m-knopf" disabled={seite <= 0}
                    onClick={() => onSeiteChange(seite - 1)}>
              ← Zurück
            </button>
            <button type="button" className="m-knopf" disabled={seite + 1 >= seiten}
                    onClick={() => onSeiteChange(seite + 1)}>
              Weiter →
            </button>
          </>
        )}
        leer={(
          <EmptyState
            titel="Keine Kollision für diese Filter."
            erklaerung={'Entweder ist hier nichts mehr offen, oder die Filter sind zu '
              + 'eng gesetzt. Die Trefferzahl oben zählt den ganzen Stand.'}
          />
        )}
      />

      {ablehnenOffen && (
        <FormSection
          titel="Ablehnen mit Grund"
          spalten={2}
          fuss={(
            <>
              <button type="button" className="m-knopf"
                      onClick={() => setAblehnenOffen(false)}>
                Abbrechen
              </button>
              <button type="button" className="m-knopf m-knopf-haupt" disabled={!grund}
                      onClick={() => setFrage('abgelehnt')}>
                Bestätigen
              </button>
            </>
          )}
        >
          <Feld
            art="auswahl"
            beschriftung="Grund"
            wert={grund}
            beiAenderung={(w) => setGrund(w as string)}
            pflicht
            optionen={[
              { wert: '', beschriftung: 'Grund wählen …' },
              ...(ablehngruende ?? []).map((g: any) => ({
                wert: g.schluessel as string, beschriftung: g.text as string,
              })),
            ]}
          />
          <Feld
            beschriftung="Angabe"
            wert={angabe}
            beiAenderung={(w) => setAngabe(w as string)}
            platzhalter="falls gefordert"
          />
        </FormSection>
      )}

      {geoeffnet && (
        <>
          {detailLaedt === geoeffnet && (
            <LoadingState text="Kollision wird geladen …" zeilen={4} />
          )}

          {detail && detailLaedt !== geoeffnet && (
            <>
              <Detailkarte d={detail} api={api} projektId={projektId} />

              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button type="button" className="m-knopf m-knopf-haupt"
                  onClick={() => onEntscheiden('angenommen')}>
                  Angenommen
                </button>
                <button type="button" className="m-knopf"
                  onClick={() => onEntscheiden('umgesetzt')}>
                  Umgesetzt
                </button>
                <button type="button" className="m-knopf"
                  onClick={() => onEntscheiden('abgelehnt')}>
                  Ablehnen
                </button>
                <button type="button" className="m-knopf" onClick={onNaechste}>
                  Nächste →
                </button>
              </div>
            </>
          )}
        </>
      )}

      <ConfirmDialog
        offen={frage === 'angenommen'}
        titel={`${ausgewaehlt.size} Anweisungen als angenommen markieren?`}
        text="Die Anweisungen gelten danach als vom Planer bestätigt."
        bestaetigenText="Als angenommen markieren"
        beiBestaetigen={() => { setFrage(null); onSammelaktion('angenommen'); }}
        beiAbbrechen={() => setFrage(null)}
      />

      <ConfirmDialog
        offen={frage === 'abgelehnt'}
        art="warnung"
        titel={`${ausgewaehlt.size} Anweisungen ablehnen?`}
        text={`Grund: ${(ablehngruende ?? []).find((g: any) => g.schluessel === grund)?.text
          ?? grund}`}
        bestaetigenText="Ablehnen"
        beiBestaetigen={() => {
          setFrage(null);
          onSammelaktion('abgelehnt', grund, angabe);
          setAblehnenOffen(false);
          setGrund('');
          setAngabe('');
        }}
        beiAbbrechen={() => setFrage(null)}
      />
    </>
  );
}

/**
 * Dokument 36, Abschnitt C — die Zelle "KI-Bewertung" je Kollisionszeile:
 * ohne vorliegende Bewertung eine Checkbox (Stapelauswahl) und der Knopf
 * "KI-Bewertung anfordern"; mit vorliegender Bewertung die Kurzfassung
 * (Bewertung/Maßnahme/Weicher/Richtung/Betrag/Risiko), aufklappbar für
 * Prüfpunkte/Begründung, und ein Verweis in die KI-Ansicht
 * (`AiCompletion.tsx` liest `?planId=…`, siehe dort).
 */
function KiBewertungZelle({
  kiBewertung, projektId,
  ausgewaehlt, onAuswahlToggle, kiLaeuft,
  offen, onOffenToggle, onAnfordern,
}: {
  kiBewertung: KiBewertungZeile | null | undefined;
  projektId: string;
  ausgewaehlt: boolean;
  onAuswahlToggle: () => void;
  kiLaeuft: boolean;
  offen: boolean;
  onOffenToggle: () => void;
  onAnfordern: () => void;
}) {
  if (!kiBewertung) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <input type="checkbox" checked={ausgewaehlt} disabled={kiLaeuft}
          onChange={onAuswahlToggle} title="Für den KI-Stapel auswählen" />
        <button type="button" className="m-knopf" disabled={kiLaeuft}
          style={{ fontSize: 11, padding: '2px 6px' }}
          onClick={onAnfordern}>
          KI-Bewertung anfordern
        </button>
      </div>
    );
  }

  return (
    <div style={{ fontSize: 11 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <StatusBadge
          klein
          zustand={farbzustand(BEWERTUNG_FARBE[kiBewertung.bewertung ?? ''])}
          text={kiBewertung.bewertung ?? 'unklar'}
        />
        {kiBewertung.risiko && (
          <StatusBadge
            klein
            zustand={farbzustand(RISIKO_FARBE[kiBewertung.risiko])}
            text={`Risiko ${kiBewertung.risiko}`}
          />
        )}
        <button type="button" className="m-knopf" style={{ fontSize: 10, padding: '1px 5px' }}
          onClick={onOffenToggle}>
          {offen ? '▾' : '▸'} Details
        </button>
        <Link to={`/projekte/${projektId}/ai-completion?planId=${kiBewertung.planId}`}
          className="schwach" style={{ fontSize: 10 }}>
          in KI-Ansicht öffnen
        </Link>
      </div>

      {(kiBewertung.massnahme || kiBewertung.weicher) && (
        <div style={{ marginTop: 3 }}>
          {kiBewertung.massnahme && <div>{kiBewertung.massnahme}</div>}
          {kiBewertung.weicher && (
            <div className="schwach">
              Weicher: {kiBewertung.weicher}
              {kiBewertung.richtung ? ` · Richtung ${kiBewertung.richtung}` : ''}
              {kiBewertung.betragMm ? ` · ${kiBewertung.betragMm} mm` : ''}
            </div>
          )}
        </div>
      )}

      {offen && (
        <div style={{ marginTop: 4, paddingTop: 4, borderTop: '1px solid var(--linie)' }}>
          {kiBewertung.begruendung && (
            <div className="schwach" style={{ marginBottom: 4 }}>
              {kiBewertung.begruendung}
            </div>
          )}
          {kiBewertung.pruefpunkte?.length > 0 && (
            <ul style={{ margin: 0, paddingLeft: 16 }}>
              {kiBewertung.pruefpunkte.map((punkt: string) => (
                <li key={punkt}>{punkt}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
